import React from 'react';
import { MiniFrame, FrameButtons } from '@code-hike/mini-frame';
import { useClasser } from '@code-hike/classer';
import { Code } from '@code-hike/smooth-code';
import { InnerTerminal } from '@code-hike/mini-terminal';
import { useSpring } from 'use-spring';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
      r,
      ar = [],
      e;

  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) {
      ar.push(r.value);
    }
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }

  return ar;
}

function __spread() {
  for (var ar = [], i = 0; i < arguments.length; i++) {
    ar = ar.concat(__read(arguments[i]));
  }

  return ar;
}

var ColorName;

(function (ColorName) {
  ColorName[ColorName["EditorBackground"] = 0] = "EditorBackground";
  ColorName[ColorName["ActiveTabBackground"] = 1] = "ActiveTabBackground";
  ColorName[ColorName["ActiveTabForeground"] = 2] = "ActiveTabForeground";
  ColorName[ColorName["InactiveTabBackground"] = 3] = "InactiveTabBackground";
  ColorName[ColorName["InactiveTabForeground"] = 4] = "InactiveTabForeground";
  ColorName[ColorName["EditorGroupBorder"] = 5] = "EditorGroupBorder";
  ColorName[ColorName["EditorGroupHeaderBackground"] = 6] = "EditorGroupHeaderBackground";
  ColorName[ColorName["TabBorder"] = 7] = "TabBorder";
  ColorName[ColorName["ActiveTabBottomBorder"] = 8] = "ActiveTabBottomBorder";
})(ColorName || (ColorName = {}));

var contrastBorder = "#6FC3DF";

function getColor(theme, colorName) {
  var colors = theme.colors || {};

  switch (colorName) {
    case ColorName.EditorBackground:
      return colors["editor.background"] || getDefault(theme, {
        light: "#fffffe",
        dark: "#1E1E1E",
        hc: "#000000"
      });

    case ColorName.ActiveTabBackground:
      return colors["tab.activeBackground"] || getColor(theme, ColorName.EditorBackground);

    case ColorName.ActiveTabForeground:
      return colors["tab.activeForeground"] || getDefault(theme, {
        dark: "#ffffff",
        light: "#333333",
        hc: "#ffffff"
      });

    case ColorName.InactiveTabBackground:
      return colors["tab.inactiveBackground"] || getDefault(theme, {
        dark: "#2D2D2D",
        light: "#ECECEC",
        hc: undefined
      });

    case ColorName.InactiveTabForeground:
      return colors["tab.inactiveForeground"] || getDefault(theme, {
        dark: transparent(getColor(theme, ColorName.ActiveTabForeground), 0.5),
        light: transparent(getColor(theme, ColorName.ActiveTabForeground), 0.7),
        hc: "#ffffff"
      });

    case ColorName.TabBorder:
      return colors["tab.border"] || getDefault(theme, {
        dark: "#252526",
        light: "#F3F3F3",
        hc: contrastBorder
      });

    case ColorName.ActiveTabBottomBorder:
      return colors["tab.activeBorder"] || getColor(theme, ColorName.ActiveTabBackground);

    case ColorName.EditorGroupBorder:
      return colors["editorGroup.border"] || getDefault(theme, {
        dark: "#444444",
        light: "#E7E7E7",
        hc: contrastBorder
      });

    case ColorName.EditorGroupHeaderBackground:
      return colors["editorGroupHeader.tabsBackground"] || getDefault(theme, {
        dark: "#252526",
        light: "#F3F3F3",
        hc: undefined
      });

    default:
      return "#f00";
  }
}

function transparent(color, opacity) {
  var _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);

  return !color ? color : color + _opacity.toString(16).toUpperCase();
}

function getDefault(theme, defaults) {
  var _a;

  var themeType = theme.type ? theme.type : ((_a = theme.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes("light")) ? "light" : "dark";
  return defaults[themeType];
} // defaults from: https://github.com/microsoft/vscode/blob/main/src/vs/workbench/common/theme.ts


var DEFAULT_HEIGHT = 200;
var EditorFrame = React.forwardRef(function InnerEditorFrame(_a, ref) {
  var _b;

  var northPanel = _a.northPanel,
      southPanel = _a.southPanel,
      terminalPanel = _a.terminalPanel,
      style = _a.style,
      height = _a.height,
      button = _a.button,
      theme = _a.theme,
      className = _a.className,
      rest = __rest(_a, ["northPanel", "southPanel", "terminalPanel", "style", "height", "button", "theme", "className"]);

  var c = useClasser("ch-editor");
  return React.createElement(MiniFrame, _assign({
    ref: ref,
    style: _assign((_b = {
      height: height !== null && height !== void 0 ? height : DEFAULT_HEIGHT
    }, _b["--ch-content-background"] = getColor(theme, ColorName.EditorGroupHeaderBackground), _b), style),
    className: c("frame") + " " + className,
    overflow: "unset",
    titleBar: React.createElement(TabsContainer, {
      tabs: northPanel.tabs,
      showFrameButtons: true,
      button: button,
      panel: "north",
      theme: theme
    })
  }, rest), React.createElement("div", {
    "data-ch-panel": "north",
    className: c("body"),
    style: northPanel.style,
    children: northPanel.children
  }), southPanel && React.createElement("div", {
    "data-ch-panel": "south",
    style: _assign({
      display: "flex",
      flexDirection: "column"
    }, southPanel.style)
  }, React.createElement("div", {
    className: "ch-frame-title-bar",
    style: {
      background: "none"
    }
  }, React.createElement(TabsContainer, {
    tabs: southPanel.tabs,
    showFrameButtons: false,
    topBorder: true,
    panel: "south",
    theme: theme
  })), React.createElement("div", {
    className: c("body"),
    children: southPanel.children,
    style: {
      flexGrow: 1,
      flexShrink: 1,
      minHeight: 0
    }
  })), terminalPanel);
});

function TabsContainer(_a) {
  var tabs = _a.tabs,
      button = _a.button,
      showFrameButtons = _a.showFrameButtons,
      topBorder = _a.topBorder,
      panel = _a.panel,
      theme = _a.theme;
  var c = useClasser("ch-editor-tab");
  return React.createElement(React.Fragment, null, topBorder && React.createElement("div", {
    style: {
      position: "absolute",
      height: "1px",
      background: getColor(theme, ColorName.EditorGroupBorder),
      width: "100%",
      top: 0,
      zIndex: 1
    }
  }), showFrameButtons ? React.createElement(FrameButtons, null) : React.createElement("div", null), tabs.map(function (_a) {
    var title = _a.title,
        active = _a.active,
        style = _a.style;
    return React.createElement("div", {
      key: title,
      title: title,
      "data-ch-tab": panel,
      className: c("", active ? "active" : "inactive"),
      style: _assign(_assign({}, style), {
        background: getColor(theme, active ? ColorName.ActiveTabBackground : ColorName.InactiveTabBackground),
        color: getColor(theme, active ? ColorName.ActiveTabForeground : ColorName.InactiveTabForeground),
        borderRightColor: getColor(theme, ColorName.TabBorder),
        borderBottomColor: getColor(theme, active ? ColorName.ActiveTabBottomBorder : ColorName.InactiveTabBackground)
      })
    }, React.createElement("div", null, title));
  }), React.createElement("div", {
    style: {
      flex: 1
    }
  }), button);
}

function getPanelStyles(prev, next, t) {
  // +---+---+
  // | x | x |
  // +---+---+
  // |   |   |
  // +---+---+
  if (prev.southHeight === null && next.southHeight === null) {
    return {
      northStyle: {
        height: prev.northHeight
      }
    };
  } // +---+---+
  // | x | x |
  // +---+---+
  // | y |   |
  // +---+---+


  if (prev.southHeight !== null && next.southHeight === null && next.northKey !== prev.southKey) {
    return {
      northStyle: {
        height: tween(prev.northHeight, next.northHeight, t)
      },
      southStyle: {
        height: prev.southHeight
      }
    };
  } // +---+---+
  // | x | y |
  // +---+---+
  // | y |   |
  // +---+---+


  if (prev.southHeight !== null && next.southHeight === null && prev.southKey === next.northKey) {
    return {
      northStyle: {
        height: prev.northHeight
      },
      southStyle: {
        position: "relative",
        height: tween(prev.southHeight, next.northHeight + next.titleBarHeight, t),
        transform: "translateY(" + tween(0, -(prev.northHeight + prev.titleBarHeight), t) + "px)"
      }
    };
  } // +---+---+
  // | x | x |
  // +---+---+
  // |   | y |
  // +---+---+


  if (prev.southHeight === null && next.southHeight !== null && prev.northKey !== next.southKey) {
    return {
      northStyle: {
        height: tween(prev.northHeight, next.northHeight, t)
      },
      southStyle: {
        position: "relative",
        height: next.southHeight
      }
    };
  } // +---+---+
  // | y | x |
  // +---+---+
  // |   | y |
  // +---+---+


  if (prev.southHeight === null && next.southHeight !== null && prev.northKey === next.southKey) {
    return {
      northStyle: {
        height: next.northHeight
      },
      southStyle: {
        position: "relative",
        height: tween(prev.northHeight + prev.titleBarHeight, next.southHeight, t),
        transform: "translateY(" + tween(-(next.northHeight + next.titleBarHeight), 0, t) + "px)"
      }
    };
  } // +---+---+
  // | x | x |
  // +---+---+
  // | y | y |
  // +---+---+


  return {
    northStyle: {
      height: tween(prev.northHeight, next.northHeight, t)
    },
    southStyle: {
      height: tween(prev.southHeight, next.southHeight, t)
    }
  };
}

function tween(a, b, t) {
  return a + (b - a) * t;
}

var useLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

function useSnapshots(ref, prev, next) {
  var _a = __read(React.useState({
    prevSnapshot: null,
    nextSnapshot: null
  }), 2),
      _b = _a[0],
      prevSnapshot = _b.prevSnapshot,
      nextSnapshot = _b.nextSnapshot,
      setState = _a[1];

  useLayoutEffect(function () {
    if (prevSnapshot || nextSnapshot) {
      setState({
        prevSnapshot: null,
        nextSnapshot: null
      });
    }
  }, [prev, next]);
  useLayoutEffect(function () {
    if (!prevSnapshot) {
      setState(function (s) {
        return _assign(_assign({}, s), {
          prevSnapshot: _assign(_assign({}, getPanelSnapshot(ref.current, prev)), getTabsSnapshot(ref.current, prev))
        });
      });
    } else if (!nextSnapshot) {
      setState(function (s) {
        return _assign(_assign({}, s), {
          nextSnapshot: _assign(_assign({}, getPanelSnapshot(ref.current, next)), getTabsSnapshot(ref.current, next))
        });
      });
    }
  });
  return {
    prevSnapshot: prevSnapshot,
    nextSnapshot: nextSnapshot
  };
}

function getPanelSnapshot(parent, step) {
  var _a;

  var northElement = parent.querySelector("[data-ch-panel='north']");
  var southElement = parent.querySelector("[data-ch-panel='south']");
  var bar = parent.querySelector(".ch-frame-title-bar");
  return {
    titleBarHeight: bar.getBoundingClientRect().height,
    northHeight: northElement.getBoundingClientRect().height,
    northKey: step.northPanel.active,
    southHeight: (southElement === null || southElement === void 0 ? void 0 : southElement.getBoundingClientRect().height) || null,
    southKey: (_a = step.southPanel) === null || _a === void 0 ? void 0 : _a.active
  };
}

function getTabsSnapshot(parent, step) {
  var _a;

  var northTabs = Array.from(parent.querySelectorAll("[data-ch-tab='north']"));
  var southTabs = Array.from(parent.querySelectorAll("[data-ch-tab='south']"));
  return {
    northTabs: getTabsDimensions(northTabs, step.northPanel.active),
    southTabs: getTabsDimensions(southTabs, (_a = step.southPanel) === null || _a === void 0 ? void 0 : _a.active)
  };
}

function getTabsDimensions(tabElements, active) {
  if (!tabElements[0]) {
    return null;
  }

  var parent = tabElements[0].parentElement;
  var parentLeft = parent.getBoundingClientRect().left;
  var dimensions = {};
  tabElements.forEach(function (child) {
    var filename = child.getAttribute("title");
    var rect = child.getBoundingClientRect();
    dimensions[filename] = {
      left: rect.left - parentLeft,
      width: rect.width,
      active: filename === active
    };
  });
  return dimensions;
}

function getTabs(prevSnapshot, nextSnapshot, northActive, southActive, t) {
  // TODO simplify
  if (!prevSnapshot.southTabs && isPresent(southActive, prevSnapshot.northTabs)) {
    /// one to two south
    return {
      northTabs: getPanelTabs(nextSnapshot.northTabs, nextSnapshot.southTabs, prevSnapshot.southTabs, prevSnapshot.northTabs, northActive, t),
      southTabs: getPanelTabs(nextSnapshot.southTabs, nextSnapshot.northTabs, prevSnapshot.northTabs, prevSnapshot.southTabs, southActive, t)
    };
  }

  if (!nextSnapshot.southTabs && isPresent(southActive, nextSnapshot.northTabs)) {
    /// two to one south
    return {
      northTabs: getPanelTabs(nextSnapshot.southTabs, nextSnapshot.northTabs, prevSnapshot.northTabs, prevSnapshot.southTabs, northActive, t),
      southTabs: getPanelTabs(nextSnapshot.northTabs, nextSnapshot.southTabs, prevSnapshot.southTabs, prevSnapshot.northTabs, southActive, t)
    };
  }

  return {
    northTabs: getPanelTabs(nextSnapshot.northTabs, nextSnapshot.southTabs, prevSnapshot.northTabs, prevSnapshot.southTabs, northActive, t),
    southTabs: getPanelTabs(nextSnapshot.southTabs, nextSnapshot.northTabs, prevSnapshot.southTabs, prevSnapshot.northTabs, southActive, t)
  };
}

function getPanelTabs(nextSnapshot, otherNextSnapshot, prevSnapshot, otherPrevSnapshot, active, t) {
  // For each tab bar there are four types of tabs
  // - oldTabs: tabs that are present in both prev and next versions of the bar
  // - totallyNewTabs: tabs that are totally new (present in next
  // but not in any prev)
  // - migratingTabs: tabs that are come from the other bar (present
  // in next and in otherPrev)
  // - disappearingTabs: present in prev but not in next or otherNext
  var oldTabs = !nextSnapshot ? [] : Object.keys(nextSnapshot).filter(function (filename) {
    return isPresent(filename, prevSnapshot) || !prevSnapshot;
  }).map(function (filename) {
    var prev = prevSnapshot && prevSnapshot[filename];
    var next = nextSnapshot[filename];
    var dx = prev ? prev.left + (next.left - prev.left) * t : next.left;
    var width = prev ? prev.width + (next.width - prev.width) * t : next.width;
    return {
      active: filename === active,
      title: filename,
      style: {
        position: "absolute",
        transform: "translateX(" + dx + "px)",
        width: width
      }
    };
  });
  var totallyNewTabs = !nextSnapshot ? [] : Object.keys(nextSnapshot).filter(function (filename) {
    return prevSnapshot && !isPresent(filename, prevSnapshot);
  } // && !isPresent(filename, otherPrevSnapshot)
  ).map(function (filename) {
    var next = nextSnapshot[filename];
    return {
      active: filename === active,
      title: filename,
      style: {
        position: "absolute",
        transform: "translateX(" + next.left + "px)",
        opacity: t,
        width: next.width
      }
    };
  });
  !nextSnapshot ? [] : Object.keys(nextSnapshot).filter(function (filename) {
    return isPresent(filename, otherPrevSnapshot);
  }).map(function (filename) {
    var prev = otherPrevSnapshot[filename];
    var next = nextSnapshot[filename];
    var dx = next.left - prev.left;
    return {
      active: filename === active,
      title: filename,
      style: {
        position: "absolute",
        transform: "translateX(" + dx + "px)"
      }
    };
  });
  var disappearingTabs = !prevSnapshot ? [] : Object.keys(prevSnapshot).filter(function (filename) {
    return !isPresent(filename, nextSnapshot);
  } // && !isPresent(filename, otherNextSnapshot)
  ).map(function (filename) {
    var prev = prevSnapshot[filename];
    return {
      active: filename === active,
      title: filename,
      style: {
        position: "absolute",
        opacity: 1 - t,
        transform: "translateX(" + prev.left + "px)",
        width: prev.width
      }
    };
  });
  return __spread(totallyNewTabs, oldTabs, disappearingTabs);
}

function isPresent(filename, snapshot) {
  return snapshot && filename && filename in snapshot;
}

function TerminalPanel(_a) {
  var prev = _a.prev,
      next = _a.next,
      t = _a.t,
      backward = _a.backward;
  var height = getHeight({
    prev: prev,
    next: next,
    t: t,
    backward: backward
  });
  return !height ? null : React.createElement("div", {
    className: "ch-editor-terminal",
    style: {
      height: height
    }
  }, React.createElement("div", {
    className: "ch-editor-terminal-tab"
  }, React.createElement("span", null, "Terminal")), React.createElement("div", {
    className: "ch-editor-terminal-content"
  }, React.createElement(InnerTerminal, {
    steps: [{
      text: prev || ""
    }, {
      text: next || ""
    }],
    progress: t
  }), ")"));
}

function getHeight(_a) {
  var prev = _a.prev,
      next = _a.next,
      t = _a.t;
  _a.backward;
  if (!prev && !next) return 0;
  if (!prev && next) return MAX_HEIGHT * Math.min(t * 4, 1);
  if (prev && !next) return MAX_HEIGHT * Math.max(1 - t * 4, 0);
  return MAX_HEIGHT;
}

var MAX_HEIGHT = 150;
var $schema = "vscode://schemas/color-theme";
var name = "dark-plus";
var tokenColors = [{
  settings: {
    foreground: "#D4D4D4"
  }
}, {
  scope: ["meta.embedded", "source.groovy.embedded"],
  settings: {
    foreground: "#D4D4D4"
  }
}, {
  scope: "emphasis",
  settings: {
    fontStyle: "italic"
  }
}, {
  scope: "strong",
  settings: {
    fontStyle: "bold"
  }
}, {
  scope: "header",
  settings: {
    foreground: "#000080"
  }
}, {
  scope: "comment",
  settings: {
    foreground: "#6A9955"
  }
}, {
  scope: "constant.language",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: ["constant.numeric", "variable.other.enummember", "keyword.operator.plus.exponent", "keyword.operator.minus.exponent"],
  settings: {
    foreground: "#b5cea8"
  }
}, {
  scope: "constant.regexp",
  settings: {
    foreground: "#646695"
  }
}, {
  scope: "entity.name.tag",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "entity.name.tag.css",
  settings: {
    foreground: "#d7ba7d"
  }
}, {
  scope: "entity.other.attribute-name",
  settings: {
    foreground: "#9cdcfe"
  }
}, {
  scope: ["entity.other.attribute-name.class.css", "entity.other.attribute-name.class.mixin.css", "entity.other.attribute-name.id.css", "entity.other.attribute-name.parent-selector.css", "entity.other.attribute-name.pseudo-class.css", "entity.other.attribute-name.pseudo-element.css", "source.css.less entity.other.attribute-name.id", "entity.other.attribute-name.scss"],
  settings: {
    foreground: "#d7ba7d"
  }
}, {
  scope: "invalid",
  settings: {
    foreground: "#f44747"
  }
}, {
  scope: "markup.underline",
  settings: {
    fontStyle: "underline"
  }
}, {
  scope: "markup.bold",
  settings: {
    fontStyle: "bold",
    foreground: "#569cd6"
  }
}, {
  scope: "markup.heading",
  settings: {
    fontStyle: "bold",
    foreground: "#569cd6"
  }
}, {
  scope: "markup.italic",
  settings: {
    fontStyle: "italic"
  }
}, {
  scope: "markup.inserted",
  settings: {
    foreground: "#b5cea8"
  }
}, {
  scope: "markup.deleted",
  settings: {
    foreground: "#ce9178"
  }
}, {
  scope: "markup.changed",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "punctuation.definition.quote.begin.markdown",
  settings: {
    foreground: "#6A9955"
  }
}, {
  scope: "punctuation.definition.list.begin.markdown",
  settings: {
    foreground: "#6796e6"
  }
}, {
  scope: "markup.inline.raw",
  settings: {
    foreground: "#ce9178"
  }
}, {
  name: "brackets of XML/HTML tags",
  scope: "punctuation.definition.tag",
  settings: {
    foreground: "#808080"
  }
}, {
  scope: ["meta.preprocessor", "entity.name.function.preprocessor"],
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "meta.preprocessor.string",
  settings: {
    foreground: "#ce9178"
  }
}, {
  scope: "meta.preprocessor.numeric",
  settings: {
    foreground: "#b5cea8"
  }
}, {
  scope: "meta.structure.dictionary.key.python",
  settings: {
    foreground: "#9cdcfe"
  }
}, {
  scope: "meta.diff.header",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "storage",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "storage.type",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: ["storage.modifier", "keyword.operator.noexcept"],
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: ["string", "meta.embedded.assembly"],
  settings: {
    foreground: "#ce9178"
  }
}, {
  scope: "string.tag",
  settings: {
    foreground: "#ce9178"
  }
}, {
  scope: "string.value",
  settings: {
    foreground: "#ce9178"
  }
}, {
  scope: "string.regexp",
  settings: {
    foreground: "#d16969"
  }
}, {
  name: "String interpolation",
  scope: ["punctuation.definition.template-expression.begin", "punctuation.definition.template-expression.end", "punctuation.section.embedded"],
  settings: {
    foreground: "#569cd6"
  }
}, {
  name: "Reset JavaScript string interpolation expression",
  scope: ["meta.template.expression"],
  settings: {
    foreground: "#d4d4d4"
  }
}, {
  scope: ["support.type.vendored.property-name", "support.type.property-name", "variable.css", "variable.scss", "variable.other.less", "source.coffee.embedded"],
  settings: {
    foreground: "#9cdcfe"
  }
}, {
  scope: "keyword",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "keyword.control",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "keyword.operator",
  settings: {
    foreground: "#d4d4d4"
  }
}, {
  scope: ["keyword.operator.new", "keyword.operator.expression", "keyword.operator.cast", "keyword.operator.sizeof", "keyword.operator.alignof", "keyword.operator.typeid", "keyword.operator.alignas", "keyword.operator.instanceof", "keyword.operator.logical.python", "keyword.operator.wordlike"],
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "keyword.other.unit",
  settings: {
    foreground: "#b5cea8"
  }
}, {
  scope: ["punctuation.section.embedded.begin.php", "punctuation.section.embedded.end.php"],
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "support.function.git-rebase",
  settings: {
    foreground: "#9cdcfe"
  }
}, {
  scope: "constant.sha.git-rebase",
  settings: {
    foreground: "#b5cea8"
  }
}, {
  name: "coloring of the Java import and package identifiers",
  scope: ["storage.modifier.import.java", "variable.language.wildcard.java", "storage.modifier.package.java"],
  settings: {
    foreground: "#d4d4d4"
  }
}, {
  name: "this.self",
  scope: "variable.language",
  settings: {
    foreground: "#569cd6"
  }
}, {
  name: "Function declarations",
  scope: ["entity.name.function", "support.function", "support.constant.handlebars", "source.powershell variable.other.member", "entity.name.operator.custom-literal"],
  settings: {
    foreground: "#DCDCAA"
  }
}, {
  name: "Types declaration and references",
  scope: ["meta.return-type", "support.class", "support.type", "entity.name.type", "entity.name.namespace", "entity.other.attribute", "entity.name.scope-resolution", "entity.name.class", "storage.type.numeric.go", "storage.type.byte.go", "storage.type.boolean.go", "storage.type.string.go", "storage.type.uintptr.go", "storage.type.error.go", "storage.type.rune.go", "storage.type.cs", "storage.type.generic.cs", "storage.type.modifier.cs", "storage.type.variable.cs", "storage.type.annotation.java", "storage.type.generic.java", "storage.type.java", "storage.type.object.array.java", "storage.type.primitive.array.java", "storage.type.primitive.java", "storage.type.token.java", "storage.type.groovy", "storage.type.annotation.groovy", "storage.type.parameters.groovy", "storage.type.generic.groovy", "storage.type.object.array.groovy", "storage.type.primitive.array.groovy", "storage.type.primitive.groovy"],
  settings: {
    foreground: "#4EC9B0"
  }
}, {
  name: "Types declaration and references, TS grammar specific",
  scope: ["meta.type.cast.expr", "meta.type.new.expr", "support.constant.math", "support.constant.dom", "support.constant.json", "entity.other.inherited-class"],
  settings: {
    foreground: "#4EC9B0"
  }
}, {
  name: "Control flow / Special keywords",
  scope: ["keyword.control", "source.cpp keyword.operator.new", "keyword.operator.delete", "keyword.other.using", "keyword.other.operator", "entity.name.operator"],
  settings: {
    foreground: "#C586C0"
  }
}, {
  name: "Variable and parameter name",
  scope: ["variable", "meta.definition.variable.name", "support.variable", "entity.name.variable", "constant.other.placeholder"],
  settings: {
    foreground: "#9CDCFE"
  }
}, {
  name: "Constants and enums",
  scope: ["variable.other.constant", "variable.other.enummember"],
  settings: {
    foreground: "#4FC1FF"
  }
}, {
  name: "Object keys, TS grammar specific",
  scope: ["meta.object-literal.key"],
  settings: {
    foreground: "#9CDCFE"
  }
}, {
  name: "CSS property value",
  scope: ["support.constant.property-value", "support.constant.font-name", "support.constant.media-type", "support.constant.media", "constant.other.color.rgb-value", "constant.other.rgb-value", "support.constant.color"],
  settings: {
    foreground: "#CE9178"
  }
}, {
  name: "Regular expression groups",
  scope: ["punctuation.definition.group.regexp", "punctuation.definition.group.assertion.regexp", "punctuation.definition.character-class.regexp", "punctuation.character.set.begin.regexp", "punctuation.character.set.end.regexp", "keyword.operator.negation.regexp", "support.other.parenthesis.regexp"],
  settings: {
    foreground: "#CE9178"
  }
}, {
  scope: ["constant.character.character-class.regexp", "constant.other.character-class.set.regexp", "constant.other.character-class.regexp", "constant.character.set.regexp"],
  settings: {
    foreground: "#d16969"
  }
}, {
  scope: ["keyword.operator.or.regexp", "keyword.control.anchor.regexp"],
  settings: {
    foreground: "#DCDCAA"
  }
}, {
  scope: "keyword.operator.quantifier.regexp",
  settings: {
    foreground: "#d7ba7d"
  }
}, {
  scope: "constant.character",
  settings: {
    foreground: "#569cd6"
  }
}, {
  scope: "constant.character.escape",
  settings: {
    foreground: "#d7ba7d"
  }
}, {
  scope: "entity.name.label",
  settings: {
    foreground: "#C8C8C8"
  }
}];
var semanticTokenColors = {
  newOperator: "#C586C0",
  stringLiteral: "#ce9178",
  customLiteral: "#DCDCAA",
  numberLiteral: "#b5cea8"
};
var colors = {
  "editor.background": "#1E1E1E",
  "editor.foreground": "#D4D4D4",
  "editor.inactiveSelectionBackground": "#3A3D41",
  "editorIndentGuide.background": "#404040",
  "editorIndentGuide.activeBackground": "#707070",
  "editor.selectionHighlightBackground": "#ADD6FF26",
  "list.dropBackground": "#383B3D",
  "activityBarBadge.background": "#007ACC",
  "sideBarTitle.foreground": "#BBBBBB",
  "input.placeholderForeground": "#A6A6A6",
  "menu.background": "#252526",
  "menu.foreground": "#CCCCCC",
  "statusBarItem.remoteForeground": "#FFF",
  "statusBarItem.remoteBackground": "#16825D",
  "ports.iconRunningProcessForeground": "#369432",
  "sideBarSectionHeader.background": "#0000",
  "sideBarSectionHeader.border": "#ccc3",
  "tab.lastPinnedBorder": "#ccc3",
  "list.activeSelectionIconForeground": "#FFF"
};
var DEFAULT_THEME = {
  $schema: $schema,
  name: name,
  tokenColors: tokenColors,
  semanticTokenColors: semanticTokenColors,
  colors: colors
};
var DEFAULT_STEP = {
  files: [{
    code: "",
    lang: "js",
    name: ""
  }],
  northPanel: {
    active: "",
    tabs: [""],
    heightRatio: 1
  }
};

function MiniEditorTween(_a) {
  var _b = _a.prev,
      prev = _b === void 0 ? DEFAULT_STEP : _b,
      _c = _a.next,
      next = _c === void 0 ? DEFAULT_STEP : _c,
      t = _a.t,
      backward = _a.backward,
      _d = _a.codeProps,
      codeProps = _d === void 0 ? {} : _d,
      _e = _a.frameProps,
      frameProps = _e === void 0 ? {} : _e;
  var ref = React.createRef();

  var _f = useTransition(ref, prev, next, t, backward, codeProps),
      northPanel = _f.northPanel,
      southPanel = _f.southPanel;

  var terminalPanel = React.createElement(TerminalPanel, {
    prev: prev.terminal,
    next: next.terminal,
    t: t,
    backward: backward
  });
  return React.createElement(EditorFrame, _assign({
    ref: ref
  }, frameProps, {
    northPanel: northPanel,
    southPanel: southPanel,
    terminalPanel: terminalPanel,
    theme: codeProps.theme || DEFAULT_THEME
  }));
}

function useTransition(ref, prev, next, t, backward, codeProps) {
  var _a = useSnapshots(ref, prev, next),
      prevSnapshot = _a.prevSnapshot,
      nextSnapshot = _a.nextSnapshot;

  if (!prevSnapshot) {
    return startingPosition(prev, next, codeProps);
  }

  if (!nextSnapshot) {
    return endingPosition(prev, next, codeProps);
  } // if (t === 0) {
  //   return startingPosition(prev, next, codeProps)
  // }


  if (t === 1) {
    return endingPosition(prev, next, codeProps);
  }

  var inputSouthPanel = prev.southPanel || next.southPanel;

  var _b = getStepFiles(prev, next, t == 0 || backward),
      prevNorthFile = _b.prevNorthFile,
      prevSouthFile = _b.prevSouthFile,
      nextNorthFile = _b.nextNorthFile,
      nextSouthFile = _b.nextSouthFile;

  var _c = getPanelStyles(prevSnapshot, nextSnapshot, t),
      northStyle = _c.northStyle,
      southStyle = _c.southStyle;

  var _d = getTabs(prevSnapshot, nextSnapshot, prevNorthFile.name, prevSouthFile === null || prevSouthFile === void 0 ? void 0 : prevSouthFile.name, t),
      northTabs = _d.northTabs,
      southTabs = _d.southTabs;

  return {
    northPanel: {
      tabs: northTabs,
      style: northStyle,
      children: React.createElement(CodeTransition, {
        codeProps: codeProps,
        prevFile: prevNorthFile,
        nextFile: nextNorthFile,
        t: t,
        parentHeight: northStyle.height
      })
    },
    southPanel: inputSouthPanel && {
      tabs: southTabs,
      style: southStyle,
      children: React.createElement(CodeTransition, {
        codeProps: codeProps,
        prevFile: prevSouthFile,
        nextFile: nextSouthFile,
        t: t,
        parentHeight: southStyle === null || southStyle === void 0 ? void 0 : southStyle.height
      })
    }
  };
} // Returns the t=0 state of the transition


function startingPosition(prev, next, codeProps) {
  var inputNorthPanel = prev.northPanel;
  var inputSouthPanel = prev.southPanel;

  var _a = getStepFiles(prev, next, true),
      prevNorthFile = _a.prevNorthFile,
      prevSouthFile = _a.prevSouthFile,
      nextNorthFile = _a.nextNorthFile,
      nextSouthFile = _a.nextSouthFile;

  var northHeight = inputSouthPanel ? "calc((100% - var(--ch-title-bar-height)) * " + inputNorthPanel.heightRatio + ")" : "100%";
  var southHeight = "calc((100% - var(--ch-title-bar-height)) * " + (inputSouthPanel === null || inputSouthPanel === void 0 ? void 0 : inputSouthPanel.heightRatio) + " + var(--ch-title-bar-height))";
  return {
    northPanel: {
      tabs: inputNorthPanel.tabs.map(function (title) {
        return {
          title: title,
          active: title === inputNorthPanel.active,
          style: {}
        };
      }),
      style: {
        height: northHeight
      },
      children: React.createElement(CodeTransition, {
        codeProps: codeProps,
        prevFile: prevNorthFile,
        nextFile: nextNorthFile,
        t: 0,
        parentHeight: northHeight
      })
    },
    southPanel: inputSouthPanel && {
      tabs: inputSouthPanel.tabs.map(function (title) {
        return {
          title: title,
          active: title === inputSouthPanel.active,
          style: {}
        };
      }),
      style: {
        height: "calc((100% - var(--ch-title-bar-height)) * " + inputSouthPanel.heightRatio + " + var(--ch-title-bar-height))"
      },
      children: React.createElement(CodeTransition, {
        codeProps: codeProps,
        prevFile: prevSouthFile,
        nextFile: nextSouthFile,
        t: 0,
        parentHeight: southHeight
      })
    }
  };
} // Returns the t=1 state of the transition


function endingPosition(prev, next, codeProps) {
  var _a;

  var inputNorthPanel = next.northPanel;
  var inputSouthPanel = next.southPanel;

  var _b = getStepFiles(prev, next, false),
      prevNorthFile = _b.prevNorthFile,
      prevSouthFile = _b.prevSouthFile,
      nextNorthFile = _b.nextNorthFile,
      nextSouthFile = _b.nextSouthFile; // getStepFiles return the intermediate files, we need to patch the ending state (2to1south)


  var isTwoToOneSouth = !inputSouthPanel && inputNorthPanel.active === ((_a = prev === null || prev === void 0 ? void 0 : prev.southPanel) === null || _a === void 0 ? void 0 : _a.active);

  if (isTwoToOneSouth) {
    nextNorthFile = nextSouthFile;
  }

  var northHeight = inputSouthPanel ? "calc((100% - var(--ch-title-bar-height)) * " + inputNorthPanel.heightRatio + ")" : "100%";
  var southHeight = "calc((100% - var(--ch-title-bar-height)) * " + (inputSouthPanel === null || inputSouthPanel === void 0 ? void 0 : inputSouthPanel.heightRatio) + " + var(--ch-title-bar-height))";
  return {
    northPanel: {
      tabs: inputNorthPanel.tabs.map(function (title) {
        return {
          title: title,
          active: title === inputNorthPanel.active,
          style: {}
        };
      }),
      style: {
        height: northHeight
      },
      children: React.createElement(CodeTransition, {
        codeProps: codeProps,
        prevFile: prevNorthFile,
        nextFile: nextNorthFile,
        t: 1,
        parentHeight: northHeight
      })
    },
    southPanel: inputSouthPanel && {
      tabs: inputSouthPanel.tabs.map(function (title) {
        return {
          title: title,
          active: title === inputSouthPanel.active,
          style: {}
        };
      }),
      style: {
        height: southHeight
      },
      children: React.createElement(CodeTransition, {
        codeProps: codeProps,
        prevFile: prevSouthFile,
        nextFile: nextSouthFile,
        t: 1,
        parentHeight: southHeight
      })
    }
  };
}

function CodeTransition(_a) {
  var prevFile = _a.prevFile,
      nextFile = _a.nextFile,
      t = _a.t,
      codeProps = _a.codeProps,
      parentHeight = _a.parentHeight;
  return React.createElement(Code, _assign({}, codeProps, {
    code: {
      prev: prevFile.code,
      next: nextFile.code
    },
    focus: {
      prev: prevFile.focus,
      next: nextFile.focus
    },
    annotations: {
      prev: prevFile.annotations,
      next: nextFile.annotations
    },
    progress: t,
    language: prevFile.lang,
    parentHeight: parentHeight
  }));
}
/**
 * Get the StepFiles for a transition
 * in each panel, if the prev and next active files are the same
 * we return the prev and next version of that panel
 * if the active files are different, we return the same file twice,
 * if backward is true we return the prev active file twice,
 * or else the next active file twice
 */


function getStepFiles(prev, next, backward) {
  var _a, _b; // The active file in each panel before and after:
  // +----+----+
  // | pn | nn |
  // +----+----+
  // | ps | ns |
  // +----+----+
  //


  var pn = prev.northPanel.active;
  var nn = next.northPanel.active;
  var ps = (_a = prev.southPanel) === null || _a === void 0 ? void 0 : _a.active;
  var ns = (_b = next.southPanel) === null || _b === void 0 ? void 0 : _b.active;
  var pnFile = prev.files.find(function (f) {
    return f.name === pn;
  });
  var nnFile = next.files.find(function (f) {
    return f.name === nn;
  });
  var psFile = ps ? prev.files.find(function (f) {
    return f.name === ps;
  }) : null;
  var nsFile = ns ? next.files.find(function (f) {
    return f.name === ns;
  }) : null;
  var oneToTwoSouth = !ps && pn === ns;

  if (oneToTwoSouth) {
    return {
      prevNorthFile: nnFile,
      nextNorthFile: nnFile,
      prevSouthFile: pnFile,
      nextSouthFile: nsFile
    };
  }

  var twoToOneSouth = !ns && nn === ps;

  if (twoToOneSouth) {
    return {
      prevNorthFile: pnFile,
      nextNorthFile: pnFile,
      prevSouthFile: psFile,
      nextSouthFile: nnFile
    };
  }

  var prevNorthFile = pn === nn ? pnFile : backward ? pnFile : nnFile;
  var nextNorthFile = pn === nn ? nnFile : backward ? pnFile : nnFile;
  var prevSouthFile = ps === ns ? psFile : backward ? psFile || nsFile : nsFile || psFile;
  var nextSouthFile = ps === ns ? nsFile : backward ? psFile || nsFile : nsFile || psFile;
  return {
    prevNorthFile: prevNorthFile,
    nextNorthFile: nextNorthFile,
    prevSouthFile: prevSouthFile,
    nextSouthFile: nextSouthFile
  };
}

function MiniEditorHike(_a) {
  var _b = _a.steps,
      steps = _b === void 0 ? [] : _b,
      _c = _a.progress,
      progress = _c === void 0 ? 0 : _c,
      _d = _a.backward,
      backward = _d === void 0 ? false : _d,
      frameProps = _a.frameProps,
      codeProps = _a.codeProps;
  var prevIndex = clamp(Math.floor(progress), 0, steps.length - 1);
  var nextIndex = clamp(prevIndex + 1, 0, steps.length - 1);
  var prev = steps[prevIndex];
  var next = steps[nextIndex];
  var t = clamp(progress - prevIndex, 0, steps.length - 1);
  return React.createElement(MiniEditorTween, {
    frameProps: frameProps,
    codeProps: codeProps,
    prev: prev,
    next: next,
    backward: backward,
    t: t
  });
}

function clamp(a, min, max) {
  return Math.max(Math.min(a, max), min);
}

var defaultSpring = {
  stiffness: 120,
  damping: 24,
  mass: 0.2,
  decimals: 3
};

function MiniEditor(props) {
  if ("northPanel" in props) {
    return React.createElement(TwoPanelEditor, _assign({}, props));
  } else if ("active" in props) {
    return React.createElement(SinglePanelEditor, _assign({}, props));
  } else {
    return React.createElement(SingleFileEditor, _assign({}, props));
  }
}

function SingleFileEditor(_a) {
  var _b = _a.code,
      code = _b === void 0 ? "" : _b,
      _c = _a.lang,
      lang = _c === void 0 ? "js" : _c,
      focus = _a.focus,
      _d = _a.filename,
      filename = _d === void 0 ? "" : _d,
      annotations = _a.annotations,
      terminal = _a.terminal,
      springConfig = _a.springConfig,
      props = __rest(_a, ["code", "lang", "focus", "filename", "annotations", "terminal", "springConfig"]);

  var step = React.useMemo(function () {
    var step = {
      files: [{
        name: filename,
        code: code,
        lang: lang,
        focus: focus,
        annotations: annotations
      }],
      northPanel: {
        active: filename,
        tabs: [filename],
        heightRatio: 1
      },
      terminal: terminal
    };
    return step;
  }, [code, lang, focus, filename, terminal]);

  var _e = useStepSpring(step, springConfig),
      prev = _e.prev,
      next = _e.next,
      t = _e.t;

  return React.createElement(MiniEditorTween, _assign({
    t: t,
    backward: false,
    prev: prev,
    next: next
  }, props));
}

function SinglePanelEditor(_a) {
  var files = _a.files,
      active = _a.active,
      terminal = _a.terminal,
      springConfig = _a.springConfig,
      props = __rest(_a, ["files", "active", "terminal", "springConfig"]);

  var step = React.useMemo(function () {
    var tabs = files.map(function (file) {
      return file.name;
    });
    var step = {
      files: files,
      northPanel: {
        active: active,
        tabs: tabs,
        heightRatio: 1
      },
      terminal: terminal
    };
    return step;
  }, [files, active, terminal]);

  var _b = useStepSpring(step, springConfig),
      prev = _b.prev,
      next = _b.next,
      t = _b.t;

  return React.createElement(MiniEditorTween, _assign({
    t: t,
    backward: false,
    prev: prev,
    next: next
  }, props));
}

function TwoPanelEditor(_a) {
  var northPanel = _a.northPanel,
      southPanel = _a.southPanel,
      files = _a.files,
      terminal = _a.terminal,
      springConfig = _a.springConfig,
      props = __rest(_a, ["northPanel", "southPanel", "files", "terminal", "springConfig"]);

  var step = React.useMemo(function () {
    return {
      northPanel: northPanel,
      southPanel: southPanel,
      files: files,
      terminal: terminal
    };
  }, [northPanel, southPanel, files, terminal]);

  var _b = useStepSpring(step, springConfig),
      prev = _b.prev,
      next = _b.next,
      t = _b.t;

  return React.createElement(MiniEditorTween, _assign({
    t: t,
    backward: false,
    prev: prev,
    next: next
  }, props));
}

function useStepSpring(step, springConfig) {
  if (springConfig === void 0) {
    springConfig = defaultSpring;
  }

  var _a = __read(React.useState({
    target: 0,
    prev: step,
    next: step
  }), 2),
      _b = _a[0],
      target = _b.target,
      prev = _b.prev,
      next = _b.next,
      setState = _a[1];

  React.useEffect(function () {
    if (next != step) {
      setState(function (s) {
        return {
          target: s.target + 1,
          prev: next,
          next: step
        };
      });
    }
  }, [step]);

  var _c = __read(useSpring(target, springConfig), 1),
      progress = _c[0];

  var t = progress % 1;
  return {
    prev: prev,
    next: next,
    t: t || 1
  };
}

function mdxToSteps(children, settings) {
  if (settings === void 0) {
    settings = {};
  }

  var steps = [];
  children.forEach(function (child, i) {
    steps.push(mdxToStep(child, steps[i - 1], settings));
  });
  return steps;
}

var defaultFileName = "index.js";

function mdxToStep(child, prev, settings) {
  var _a;

  if (settings === void 0) {
    settings = {};
  }

  var stepProps = (child === null || child === void 0 ? void 0 : child.props) || {};
  var stepChildren = React.Children.toArray(stepProps.children);
  var separatorIndex = stepChildren.findIndex(function (child) {
    var _a;

    return ((_a = child === null || child === void 0 ? void 0 : child.props) === null || _a === void 0 ? void 0 : _a.mdxType) === "hr";
  });
  var hasTwoPanels = separatorIndex !== -1;
  var northChildren = hasTwoPanels ? stepChildren.slice(0, separatorIndex) : stepChildren;
  var southChildren = hasTwoPanels ? stepChildren.slice(separatorIndex + 1) : null;
  var northFiles = northChildren.map(function (pre) {
    return preToFile(pre, prev ? prev.files : [], settings);
  });
  var southFiles = southChildren === null || southChildren === void 0 ? void 0 : southChildren.map(function (pre) {
    return preToFile(pre, prev ? prev.files : [], settings);
  });
  var prevFiles = (prev === null || prev === void 0 ? void 0 : prev.files) || [];

  var files = __spread(prevFiles.filter(function (f) {
    return !northFiles.some(function (nf) {
      return nf.name === f.name;
    }) && !(southFiles === null || southFiles === void 0 ? void 0 : southFiles.some(function (sf) {
      return sf.name === f.name;
    }));
  }), northFiles, southFiles || []);

  return {
    files: files,
    northPanel: {
      tabs: chooseNorthTabs(prev, northFiles, southFiles),
      active: chooseActiveFile(northFiles, prev === null || prev === void 0 ? void 0 : prev.northPanel.active),
      heightRatio: 0.5
    },
    southPanel: southFiles && southFiles.length ? {
      tabs: chooseSouthTabs(prev, northFiles, southFiles),
      active: chooseActiveFile(southFiles, (_a = prev === null || prev === void 0 ? void 0 : prev.southPanel) === null || _a === void 0 ? void 0 : _a.active),
      heightRatio: 0.5
    } : undefined
  };
}

function chooseNorthTabs(prev, northFiles, southFiles) {
  // old north tabs + new north tabs (except hidden) - new south tabs
  var oldNorthTabs = (prev === null || prev === void 0 ? void 0 : prev.northPanel.tabs) || [];
  var newSouthTabs = (southFiles || []).map(function (f) {
    return f.name;
  });
  var newNorthTabs = northFiles.filter(function (f) {
    return !f.hidden && !oldNorthTabs.includes(f.name);
  }).map(function (f) {
    return f.name;
  });
  var baseTabs = oldNorthTabs.filter(function (tab) {
    return !newSouthTabs.includes(tab);
  });
  return __spread(baseTabs, newNorthTabs);
}

function chooseSouthTabs(prev, northFiles, southFiles) {
  var _a; // old south tabs + new south tabs (except hidden) - new north tabs


  var oldSouthTabs = ((_a = prev === null || prev === void 0 ? void 0 : prev.southPanel) === null || _a === void 0 ? void 0 : _a.tabs) || [];
  var newSouthTabs = (southFiles || []).filter(function (f) {
    return !f.hidden && !oldSouthTabs.includes(f.name);
  }).map(function (f) {
    return f.name;
  });
  var newNorthTabs = northFiles.map(function (f) {
    return f.name;
  });
  var baseTabs = oldSouthTabs.filter(function (tab) {
    return !newNorthTabs.includes(tab);
  });
  return __spread(baseTabs, newSouthTabs);
}

function chooseActiveFile(panelFiles, prev) {
  var _a, _b;

  var active = ((_a = panelFiles.find(function (file) {
    return file.active;
  })) === null || _a === void 0 ? void 0 : _a.name) || ((_b = panelFiles[0]) === null || _b === void 0 ? void 0 : _b.name) || prev;

  if (!active) {
    throw new Error("Something is wrong with Code Hike");
  }

  return active;
}

function preToFile(preElement, prevFiles, settings) {
  var _a, _b, _c;

  var codeElementProps = ((_b = (_a = preElement === null || preElement === void 0 ? void 0 : preElement.props) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.props) || {};
  var lang = (_c = codeElementProps.className) === null || _c === void 0 ? void 0 : _c.slice(9);

  var _d = parseMetastring(codeElementProps.metastring || ""),
      name = _d.name,
      options = __rest(_d, ["name"]);

  var fileName = name || (settings === null || settings === void 0 ? void 0 : settings.defaultFileName) || defaultFileName;
  var code = codeElementProps.children;
  var prevFile = prevFiles.find(function (file) {
    return file.name === fileName;
  });
  return _assign({
    code: code.trim() === "" && prevFile ? prevFile.code : code,
    lang: lang,
    name: fileName
  }, options);
}

function parseMetastring(metastring) {
  var params = metastring.split(" ");
  var options = {};
  var name = null;
  params.forEach(function (param) {
    var _a = __read(param.split("="), 2),
        key = _a[0],
        value = _a[1];

    if (value != null) {
      options[key] = value;
    } else if (name === null) {
      name = key;
    } else {
      options[key] = true;
    }
  });
  return _assign({
    name: name
  }, options);
}

export { MiniEditor, MiniEditorHike, MiniEditorTween, mdxToStep, mdxToSteps };